import React from "react";
import { Link, useLocation } from "react-router-dom";
import { XMarkIcon } from "@heroicons/react/24/outline";
import { NAVIGATION_ITEMS } from "../constants/routes";

function classNames(...classes) {
  return classes.filter(Boolean).join(" ");
}

function SideMenu({ isOpen, onClose }) {
  const location = useLocation();

  const isActiveRoute = (path) => {
    const pathname = location.pathname;
    
    // Exact match
    if (pathname === path) {
      return true;
    }
    
    // Special handling for routes that are prefixes of other routes
    // e.g., /inventory is a prefix of /inventory/alerts
    // We want /inventory to match /inventory/items/123 but NOT /inventory/alerts
    
    // Check if this path is a prefix of another navigation item
    const isPrefixOfOtherRoute = NAVIGATION_ITEMS.some(item => 
      item.path !== path && item.path.startsWith(path + '/')
    );
    
    if (isPrefixOfOtherRoute) {
      // This path is a prefix of another route (e.g., /inventory is prefix of /inventory/alerts)
      // Only match if pathname starts with path + '/' (child routes) but NOT if it matches the other route
      // Example: /inventory should match /inventory/items/123 but NOT /inventory/alerts
      if (pathname.startsWith(path + '/')) {
        // Check if it matches any other navigation item exactly
        const matchesOtherRoute = NAVIGATION_ITEMS.some(item => 
          item.path !== path && pathname === item.path
        );
        return !matchesOtherRoute; // Match only if it doesn't match another route exactly
      }
      return false;
    }
    
    // For other routes, use startsWith matching
    return pathname.startsWith(path + '/') || pathname === path;
  };

  return (
    <>
      {/* Mobile overlay */}
      {isOpen && (
        <div
          className="fixed inset-0 bg-gray-600 bg-opacity-75 z-40 lg:hidden"
          onClick={onClose}
          aria-hidden="true"
        />
      )}

      {/* Sidebar */}
      <div
        className={classNames(
          "fixed inset-y-0 left-0 z-50 w-64 bg-white shadow-lg transform transition-transform duration-300 ease-in-out lg:translate-x-0 lg:static lg:inset-0 lg:flex-shrink-0",
          isOpen ? "translate-x-0" : "-translate-x-full lg:translate-x-0"
        )}
      >
        <div className="flex flex-col h-full">
          {/* Mobile header */}
          <div className="flex items-center justify-between p-4 border-b border-gray-200 lg:hidden">
            <div className="flex items-center gap-2">
              <img
                className="h-8 w-8"
                src={`${process.env.PUBLIC_URL || ''}/favicon.png`}
                alt="Logo"
              />
              <span className="font-bold text-gray-900">Admin Panel</span>
            </div>
            <button
              onClick={onClose}
              className="p-2 rounded-md text-gray-400 hover:text-gray-500 hover:bg-gray-100"
            >
              <XMarkIcon className="h-6 w-6" />
            </button>
          </div>

          {/* Navigation */}
          <nav className="flex-1 px-4 py-6 space-y-1 overflow-y-auto">
            {NAVIGATION_ITEMS.map((item) => {
              const isActive = isActiveRoute(item.path);
              return (
                <Link
                  key={item.name}
                  to={item.path}
                  onClick={() => {
                    // Close mobile menu on navigation
                    if (window.innerWidth < 1024) {
                      onClose();
                    }
                  }}
                  className={classNames(
                    isActive
                      ? "bg-primary text-white border-r-2 border-primary"
                      : "text-textPrimary hover:bg-secondary hover:text-textPrimary",
                    "group flex items-center gap-3 px-3 py-2 text-sm font-medium rounded-lg transition-colors"
                  )}
                >
                  <item.icon className="h-5 w-5 flex-shrink-0" />
                  <span>{item.name}</span>
                </Link>
              );
            })}
          </nav>
        </div>
      </div>
    </>
  );
}

export default SideMenu;
